import { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { parseArrayParam } from '@/hooks/useFilterParams';
import { useGridPreferences } from '@/hooks/useGridPreferences';
import { ProvisioningStatusBadge } from '@/components/tenants/ProvisioningStatusBadge';
import {
  ColumnDef,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { Filter, Search, Settings2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatPhoneUS, normalizePhoneNumber } from '@marine/utils/formatters';
import { Avatar, AvatarFallback } from '@marine/ui/avatar';
import { Badge } from '@marine/ui/badge';
import { Button } from '@marine/ui/button';
import {
  Card,
  CardFooter,
  CardHeader,
  CardHeading,
  CardTable,
  CardToolbar,
} from '@marine/ui/card';
import { Checkbox } from '@marine/ui/checkbox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@marine/ui/command';
import { DataGrid } from '@marine/ui/data-grid';
import { DataGridColumnHeader } from '@marine/ui/data-grid-column-header';
import { DataGridColumnVisibility } from '@marine/ui/data-grid-column-visibility';
import { DataGridPagination } from '@marine/ui/data-grid-pagination';
import {
  DataGridTable,
  DataGridTableRowSelect,
  DataGridTableRowSelectAll,
} from '@marine/ui/data-grid-table';
import { Input } from '@marine/ui/input';
import { Label } from '@marine/ui/label';
import { Skeleton } from '@marine/ui/skeleton';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@marine/ui/popover';
import { ScrollArea, ScrollBar } from '@marine/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@marine/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@marine/ui/dialog';
import { Textarea } from '@marine/ui/textarea';
import {
  RiMoreLine,
  RiEyeLine,
  RiEditLine,
  RiCheckLine,
  RiPauseLine,
  RiDeleteBinLine,
} from '@remixicon/react';

import { useTenants, useActivateTenant, useSuspendTenant, useDeleteTenant } from '../hooks/useTenants';
import type { Tenant } from '../types/platform.types';
import { TenantFormSheet } from '../components/TenantFormSheet';
import { TenantsPageHeader } from './tenants/TenantsPageHeader';
import { getErrorMessage } from '@marine/utils/getErrorMessage';

const STATUS_OPTIONS = [
  { id: 'active', name: 'Active', color: 'bg-success' },
  { id: 'inactive', name: 'Inactive', color: 'bg-muted' },
  { id: 'suspended', name: 'Suspended', color: 'bg-danger' },
  { id: 'pending_approval', name: 'Pending', color: 'bg-warning' },
];

const PLAN_OPTIONS = [
  { id: 'basic', name: 'Basic', color: 'bg-blue-500/10 text-blue-700 border-blue-200' },
  { id: 'professional', name: 'Professional', color: 'bg-purple-500/10 text-purple-700 border-purple-200' },
  { id: 'enterprise', name: 'Enterprise', color: 'bg-amber-500/10 text-amber-700 border-amber-200' },
];

const BUSINESS_TYPES = [
  { id: 'dealership', name: 'Dealership' },
  { id: 'distributor', name: 'Distributor' },
  { id: 'service_center', name: 'Service Center' },
];

const TENANTS_GRID_KEY = 'platform-tenants';

const TENANTS_DEFAULT_COLUMN_ORDER: string[] = [
  'id',
  'name',
  'business_type',
  'plan',
  'domain',
  'phone',
  'max_users',
  'max_boats',
  'status',
  'created_by',
  'updated_by',
  'updated_at',
  'created',
  'actions',
];

const TENANTS_GRID_DEFAULTS = {
  columnVisibility: {} as Record<string, boolean>,
  columnOrder: TENANTS_DEFAULT_COLUMN_ORDER,
  columnSizing: {} as Record<string, number>,
  pageSize: 15,
  sorting: [{ id: 'name', desc: false }],
};

function TenantsListPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(() => parseArrayParam(searchParams, 'status'));
  const [selectedPlans, setSelectedPlans] = useState<string[]>(() => parseArrayParam(searchParams, 'plan'));
  const grid = useGridPreferences(TENANTS_GRID_KEY, TENANTS_GRID_DEFAULTS);
  const { pagination, sorting, columnOrder, columnVisibility, columnSizing } = grid.state;
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('search') || '');
  const [statusSearch, setStatusSearch] = useState('');
  const [planSearch, setPlanSearch] = useState('');

  useEffect(() => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      const set = (key: string, value: string | null) => {
        if (value) params.set(key, value);
        else params.delete(key);
      };
      set('search', searchQuery.trim() || null);
      set('status', selectedStatuses.length ? selectedStatuses.join(',') : null);
      set('plan', selectedPlans.length ? selectedPlans.join(',') : null);
      return params;
    }, { replace: true });
  }, [searchQuery, selectedStatuses, selectedPlans, setSearchParams]);

  // Filter change -> back to page 1.
  useEffect(() => {
    grid.resetPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, selectedStatuses, selectedPlans]);

  const [formDialog, setFormDialog] = useState<{ open: boolean; tenant: Tenant | null }>({
    open: false,
    tenant: null,
  });

  const [suspendDialog, setSuspendDialog] = useState<{ open: boolean; tenant: Tenant | null }>({
    open: false,
    tenant: null,
  });
  const [suspendReason, setSuspendReason] = useState('');

  const { data, isLoading, isError, error } = useTenants({
    page: pagination.pageIndex + 1,
    per_page: pagination.pageSize,
  });

  const { mutate: activateTenant } = useActivateTenant();
  const { mutate: suspendTenant } = useSuspendTenant();
  const { mutate: deleteTenant } = useDeleteTenant();

  // CRITICAL: Memoize tenants array to prevent cascading re-renders
  const tenants = useMemo(() => data?.items || [], [data]);

  // Calculate counts for filters
  const statusCounts = useMemo(() => {
    return tenants.reduce(
      (acc, tenant) => {
        acc[tenant.status] = (acc[tenant.status] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );
  }, [tenants]);

  const planCounts = useMemo(() => {
    return tenants.reduce(
      (acc, tenant) => {
        acc[tenant.subscription_plan] = (acc[tenant.subscription_plan] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );
  }, [tenants]);

  // Filter data
  const filteredData = useMemo(() => {
    return tenants.filter((tenant) => {
      const matchesStatus =
        !selectedStatuses.length || selectedStatuses.includes(tenant.status);
      const matchesPlan =
        !selectedPlans.length || selectedPlans.includes(tenant.subscription_plan);

      const searchLower = searchQuery.toLowerCase();
      const searchDigits = normalizePhoneNumber(searchQuery);
      const tenantPhone = tenant.phone ?? '';
      const tenantPhoneDigits = normalizePhoneNumber(tenantPhone);
      const matchesSearch =
        !searchQuery ||
        tenant.name.toLowerCase().includes(searchLower) ||
        tenant.email.toLowerCase().includes(searchLower) ||
        tenant.business_type.toLowerCase().includes(searchLower) ||
        tenantPhone.toLowerCase().includes(searchLower) ||
        (searchDigits.length > 0 && tenantPhoneDigits.includes(searchDigits));

      return matchesStatus && matchesPlan && matchesSearch;
    });
  }, [tenants, searchQuery, selectedStatuses, selectedPlans]);

  const handleStatusChange = (checked: boolean, status: string) => {
    setSelectedStatuses((prev) =>
      checked ? [...prev, status] : prev.filter((s) => s !== status),
    );
  };

  const handlePlanChange = (checked: boolean, plan: string) => {
    setSelectedPlans((prev) =>
      checked ? [...prev, plan] : prev.filter((p) => p !== plan),
    );
  };

  const filteredStatuses = useMemo(() => {
    if (!statusSearch) return STATUS_OPTIONS;
    const lower = statusSearch.toLowerCase();
    return STATUS_OPTIONS.filter((item) =>
      item.name.toLowerCase().includes(lower),
    );
  }, [statusSearch]);

  const filteredPlans = useMemo(() => {
    if (!planSearch) return PLAN_OPTIONS;
    const lower = planSearch.toLowerCase();
    return PLAN_OPTIONS.filter((item) =>
      item.name.toLowerCase().includes(lower),
    );
  }, [planSearch]);

  const handleSuspend = () => {
    if (suspendDialog.tenant) {
      suspendTenant(
        { id: suspendDialog.tenant.id, reason: suspendReason },
        {
          onSuccess: () => {
            setSuspendDialog({ open: false, tenant: null });
            setSuspendReason('');
          },
        },
      );
    }
  };

  const getStatusBadge = (status: string) => {
    const statusOption = STATUS_OPTIONS.find((s) => s.id === status);
    const variantMap: Record<string, "secondary" | "destructive" | "outline" | "primary" | "success" | "warning" | "info" | null | undefined> = {
      active: 'success',
      inactive: 'secondary',
      suspended: 'destructive',
      pending_approval: 'warning',
    };
    return (
      <Badge variant={variantMap[status] || 'secondary'} size="sm">
        {statusOption?.name || status}
      </Badge>
    );
  };

  const getPlanBadge = (plan: string) => {
    const planOption = PLAN_OPTIONS.find((p) => p.id === plan);
    return (
      <Badge className={cn('shrink-0', planOption?.color)} size="sm">
        {planOption?.name || plan}
      </Badge>
    );
  };

  const columns = useMemo<ColumnDef<Tenant>[]>(
    () => [
      {
        accessorKey: 'id',
        id: 'id',
        header: () => <DataGridTableRowSelectAll size="sm" />,
        cell: ({ row }) => <DataGridTableRowSelect row={row} size="sm" />,
        enableSorting: false,
        size: 40,
        meta: {
          headerClassName: 'ps-4',
          cellClassName: 'ps-4',
          skeleton: <></>,
        },
        enableHiding: false,
        enableResizing: false,
      },
      {
        accessorKey: 'name',
        id: 'name',
        header: ({ column }) => (
          <DataGridColumnHeader
            title="Tenant"
            visibility={true}
            column={column}
          />
        ),
        cell: ({ row }) => {
          const initials = row.original.name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);

            return (
              <div className="group flex items-center gap-2">
                <Link to={`/platform/tenants/${row.original.id}`} className="cursor-pointer">
                  <Avatar className="size-7 rounded-lg">
                    <AvatarFallback className="border-0 text-xs font-semibold bg-primary/10 text-primary rounded-lg">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                </Link>
                <div className="flex flex-col">
                  <div className="flex items-center gap-1.5">
                    <Link
                      to={`/platform/tenants/${row.original.id}`}
                      className="font-medium text-foreground group-hover:text-link transition-colors duration-200"
                    >
                      {row.original.name}
                    </Link>
                    <ProvisioningStatusBadge status={row.original.provisioning_status} />
                  </div>
                  <Link
                    to={`mailto:${row.original.email}`}
                    className="text-xs text-muted-foreground hover:text-link transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {row.original.email}
                  </Link>
                </div>
              </div>
            );
        },
        size: 280,
        enableSorting: true,
        enableHiding: false,
        enableResizing: true,
        meta: {
          skeleton: (
            <div className="flex items-center gap-2">
              <Skeleton className="size-7 shrink-0 rounded-lg" />
              <div className="flex flex-col gap-1.5">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-40" />
              </div>
            </div>
          ),
        },
      },
      {
        accessorKey: 'business_type',
        id: 'business_type',
        header: ({ column }) => (
          <DataGridColumnHeader
            title="Business Type"
            visibility={true}
            column={column}
          />
        ),
        cell: ({ row }) => {
          const type = BUSINESS_TYPES.find((t) => t.id === row.original.business_type);
          return (
            <span className="text-sm text-foreground">
              {type?.name || row.original.business_type}
            </span>
          );
        },
        size: 150,
        enableSorting: true,
        enableHiding: true,
        enableResizing: true,
        meta: {
          skeleton: <Skeleton className="h-4 w-24" />,
        },
      },
      {
        accessorKey: 'subscription_plan',
        id: 'plan',
        header: ({ column }) => (
          <DataGridColumnHeader
            title="Plan"
            visibility={true}
            column={column}
          />
        ),
        cell: ({ row }) => getPlanBadge(row.original.subscription_plan),
        size: 150,
        enableSorting: true,
        enableHiding: true,
        enableResizing: true,
        meta: {
          skeleton: <Skeleton className="h-5 w-24 rounded-full" />,
        },
      },
      {
        accessorKey: 'subdomain',
        id: 'domain',
        header: ({ column }) => (
          <DataGridColumnHeader
            title="Domain"
            visibility={true}
            column={column}
          />
        ),
        cell: ({ row }) => {
          const domain = row.original.domain || `${row.original.subdomain}.marine-dms.com`;
          return (
            <span className="text-sm text-muted-foreground font-mono">
              {domain}
            </span>
          );
        },
        size: 220,
        enableSorting: true,
        enableHiding: true,
        enableResizing: true,
        meta: {
          skeleton: <Skeleton className="h-4 w-44" />,
        },
      },
      {
        accessorKey: 'phone',
        id: 'phone',
        header: ({ column }) => (
          <DataGridColumnHeader
            title="Phone"
            visibility={true}
            column={column}
          />
        ),
        cell: ({ row }) => (
          <span className="text-sm text-foreground">
            {row.original.phone ? formatPhoneUS(row.original.phone) : '-'}
          </span>
        ),
        size: 140,
        enableSorting: true,
        enableHiding: true,
        enableResizing: true,
        meta: {
          skeleton: <Skeleton className="h-4 w-28" />,
        },
      },
      {
        accessorKey: 'max_users',
        id: 'max_users',
        header: ({ column }) => (
          <DataGridColumnHeader
            title="Max Users"
            visibility={true}
            column={column}
          />
        ),
        cell: ({ row }) => (
          <span className="text-sm text-foreground tabular-nums">
            {row.original.max_users}
          </span>
        ),
        size: 100,
        enableSorting: true,
        enableHiding: true,
        enableResizing: true,
        meta: {
          skeleton: <Skeleton className="h-4 w-12" />,
        },
      },
      {
        accessorKey: 'max_boats',
        id: 'max_boats',
        header: ({ column }) => (
          <DataGridColumnHeader
            title="Max Boats"
            visibility={true}
            column={column}
          />
        ),
        cell: ({ row }) => (
          <span className="text-sm text-foreground tabular-nums">
            {row.original.max_boats}
          </span>
        ),
        size: 110,
        enableSorting: true,
        enableHiding: true,
        enableResizing: true,
        meta: {
          skeleton: <Skeleton className="h-4 w-12" />,
        },
      },
      {
        accessorKey: 'status',
        id: 'status',
        header: ({ column }) => (
          <DataGridColumnHeader
            title="Status"
            visibility={true}
            column={column}
          />
        ),
        cell: ({ row }) => getStatusBadge(row.original.status),
        size: 120,
        enableSorting: true,
        enableHiding: true,
        enableResizing: true,
        meta: {
          skeleton: <Skeleton className="h-5 w-16 rounded-full" />,
        },
      },
      {
        accessorKey: 'created_by',
        id: 'created_by',
        header: ({ column }) => (
          <DataGridColumnHeader
            title="Created By"
            visibility={true}
            column={column}
          />
        ),
        cell: ({ row }) => {
          const createdBy = row.original.created_by;
          if (!createdBy) return <span className="text-sm text-muted-foreground">-</span>;
          
          const initials = createdBy.full_name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
          
          return (
            <div className="flex items-center gap-2">
              <Avatar className="size-6">
                <AvatarFallback className="text-[10px] font-semibold bg-muted text-foreground">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm text-foreground truncate">
                {createdBy.full_name}
              </span>
            </div>
          );
        },
        size: 160,
        enableSorting: false,
        enableHiding: true,
        enableResizing: true,
        meta: {
          skeleton: (
            <div className="flex items-center gap-2">
              <Skeleton className="size-6 rounded-full shrink-0" />
              <Skeleton className="h-4 w-24" />
            </div>
          ),
        },
      },
      {
        accessorKey: 'updated_by',
        id: 'updated_by',
        header: ({ column}) => (
          <DataGridColumnHeader
            title="Updated By"
            visibility={true}
            column={column}
          />
        ),
        cell: ({ row }) => {
          const updatedBy = row.original.updated_by;
          if (!updatedBy) return <span className="text-sm text-muted-foreground">-</span>;
          
          const initials = updatedBy.full_name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
          
          return (
            <div className="flex items-center gap-2">
              <Avatar className="size-6">
                <AvatarFallback className="text-[10px] font-semibold bg-muted text-foreground">
                  {initials}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm text-foreground truncate">
                {updatedBy.full_name}
              </span>
            </div>
          );
        },
        size: 160,
        enableSorting: false,
        enableHiding: true,
        enableResizing: true,
        meta: {
          skeleton: (
            <div className="flex items-center gap-2">
              <Skeleton className="size-6 rounded-full shrink-0" />
              <Skeleton className="h-4 w-24" />
            </div>
          ),
        },
      },
      {
        accessorKey: 'updated_at',
        id: 'updated_at',
        header: ({ column }) => (
          <DataGridColumnHeader
            title="Updated"
            visibility={true}
            column={column}
          />
        ),
        cell: ({ row }) => {
          const date = new Date(row.original.updated_at);
          return (
            <span className="text-sm text-foreground">
              {date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </span>
          );
        },
        size: 140,
        enableSorting: true,
        enableHiding: true,
        enableResizing: true,
        meta: {
          skeleton: <Skeleton className="h-4 w-20" />,
        },
      },
      {
        accessorKey: 'created_at',
        id: 'created',
        header: ({ column }) => (
          <DataGridColumnHeader
            title="Created"
            visibility={true}
            column={column}
          />
        ),
        cell: ({ row }) => {
          const date = new Date(row.original.created_at);
          return (
            <span className="text-sm text-foreground">
              {date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </span>
          );
        },
        size: 140,
        enableSorting: true,
        enableHiding: true,
        enableResizing: true,
        meta: {
          skeleton: <Skeleton className="h-4 w-20" />,
        },
      },
      {
        id: 'actions',
        header: () => <span className="sr-only">Actions</span>,
        cell: ({ row }) => {
          const tenant = row.original;
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button mode="icon" variant="ghost" size="sm">
                  <RiMoreLine className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate(`/platform/tenants/${tenant.id}`)}>
                  <RiEyeLine className="w-4 h-4 mr-2" />
                  View Details
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setFormDialog({ open: true, tenant })}>
                  <RiEditLine className="w-4 h-4 mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {tenant.status === 'active' ? (
                  <DropdownMenuItem
                    onClick={() => setSuspendDialog({ open: true, tenant })}
                  >
                    <RiPauseLine className="w-4 h-4 mr-2" />
                    Suspend
                  </DropdownMenuItem>
                ) : tenant.status === 'suspended' ? (
                  <DropdownMenuItem onClick={() => activateTenant(tenant.id)}>
                    <RiCheckLine className="w-4 h-4 mr-2" />
                    Activate
                  </DropdownMenuItem>
                ) : null}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive"
                  onClick={() => {
                    if (confirm('Are you sure you want to delete this tenant?')) {
                      deleteTenant(tenant.id);
                    }
                  }}
                >
                  <RiDeleteBinLine className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
        size: 60,
        enableSorting: false,
        enableHiding: false,
        meta: {
          skeleton: <></>,
        },
      },
    ],
    [navigate, activateTenant, deleteTenant],
  );

  const table = useReactTable({
    columns,
    data: filteredData,
    pageCount: Math.ceil((filteredData?.length || 0) / pagination.pageSize),
    getRowId: (row: Tenant) => row.id.toString(),
    state: {
      pagination,
      sorting,
      columnOrder,
      columnVisibility,
      columnSizing,
    },
    columnResizeMode: 'onChange',
    onPaginationChange: grid.onPaginationChange,
    onSortingChange: grid.onSortingChange,
    onColumnOrderChange: grid.onColumnOrderChange,
    onColumnVisibilityChange: grid.onColumnVisibilityChange,
    onColumnSizingChange: grid.onColumnSizingChange,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <>
      <TenantsPageHeader />
      {/* Error Display */}
      {isError && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-destructive/20 p-1">
              <svg className="h-5 w-5 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-destructive">Failed to load tenants</h3>
              <p className="mt-1 text-sm text-destructive/80">
                {getErrorMessage(error, 'An error occurred')}
              </p>
            </div>
          </div>
        </div>
      )}
      
      <DataGrid
        table={table}
        recordCount={filteredData?.length || 0}
        tableClassNames={{
          bodyRow: 'group/row',
        }}
        tableLayout={{
          dense: true,
          columnsPinnable: true,
          columnsResizable: true,
          columnsMovable: true,
          columnsVisibility: true,
        }}
        isLoading={isLoading}
      >
        <Card className="border-none shadow-none">
          <CardHeader className="px-4 py-3">
            <CardHeading>
              <div className="flex items-center gap-2.5">
                {/* Search */}
                <div className="relative">
                  <Search className="size-4 text-muted-foreground absolute start-3 top-1/2 -translate-y-1/2" />
                  <Input
                    variant="sm"
                    placeholder="Search tenants..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="ps-9 w-48"
                  />
                  {searchQuery.length > 0 && (
                    <Button
                      mode="icon"
                      variant="ghost"
                      className="absolute end-1.5 top-1/2 -translate-y-1/2 h-6 w-6"
                      onClick={() => setSearchQuery('')}
                    >
                      <X />
                    </Button>
                  )}
                </div>

                {/* Status Filter */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button size="sm" variant="outline">
                      <Filter className="size-3.5" />
                      Status
                      {selectedStatuses.length > 0 && (
                        <Badge size="sm" variant="outline">
                          {selectedStatuses.length}
                        </Badge>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-56 p-0" align="start">
                    <Command>
                      <CommandInput
                        placeholder="Search status..."
                        value={statusSearch}
                        onValueChange={setStatusSearch}
                      />
                      <CommandList>
                        <CommandEmpty>No status found.</CommandEmpty>
                        <CommandGroup>
                          {filteredStatuses.map((status) => {
                            const count = statusCounts[status.id] || 0;
                            return (
                              <CommandItem
                                key={status.id}
                                value={status.id}
                                className="flex items-center gap-2.5 bg-transparent!"
                              >
                                <Checkbox
                                  id={`status-${status.id}`}
                                  checked={selectedStatuses.includes(status.id)}
                                  onCheckedChange={(checked) =>
                                    handleStatusChange(checked === true, status.id)
                                  }
                                />
                                <Label
                                  htmlFor={`status-${status.id}`}
                                  className="grow flex items-center justify-between font-normal gap-1.5 cursor-pointer"
                                >
                                  <div className="inline-flex items-center gap-1.5">
                                    <span
                                      className={cn(
                                        'rounded-full size-2',
                                        status.color,
                                      )}
                                    ></span>
                                    <span className="text-sm text-foreground">
                                      {status.name}
                                    </span>
                                  </div>
                                  <span className="text-sm text-muted-foreground font-semibold">
                                    {count}
                                  </span>
                                </Label>
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>

                {/* Plan Filter */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button size="sm" variant="outline">
                      <Filter className="size-3.5" />
                      Plan
                      {selectedPlans.length > 0 && (
                        <Badge size="sm" variant="outline">
                          {selectedPlans.length}
                        </Badge>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-56 p-0" align="start">
                    <Command>
                      <CommandInput
                        placeholder="Search plan..."
                        value={planSearch}
                        onValueChange={setPlanSearch}
                      />
                      <CommandList>
                        <CommandEmpty>No plan found.</CommandEmpty>
                        <CommandGroup>
                          {filteredPlans.map((plan) => {
                            const count = planCounts[plan.id] || 0;
                            return (
                              <CommandItem
                                key={plan.id}
                                value={plan.id}
                                className="flex items-center gap-2.5 bg-transparent!"
                              >
                                <Checkbox
                                  id={`plan-${plan.id}`}
                                  checked={selectedPlans.includes(plan.id)}
                                  onCheckedChange={(checked) =>
                                    handlePlanChange(checked === true, plan.id)
                                  }
                                />
                                <Label
                                  htmlFor={`plan-${plan.id}`}
                                  className="grow flex items-center justify-between font-normal gap-1.5 cursor-pointer"
                                >
                                  <Badge className={cn('shrink-0', plan.color)} size="sm">
                                    {plan.name}
                                  </Badge>
                                  <span className="text-sm text-muted-foreground font-semibold">
                                    {count}
                                  </span>
                                </Label>
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </CardHeading>
            <CardToolbar>
              <DataGridColumnVisibility
                table={table}
                trigger={
                  <Button size="sm" variant="outline">
                    <Settings2 className="size-4" />
                    View
                  </Button>
                }
              />
            </CardToolbar>
          </CardHeader>

          <CardTable>
            <ScrollArea>
              <DataGridTable />
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </CardTable>

          <CardFooter className="px-4 py-0">
            <DataGridPagination
              className="py-1"
              sizes={[5, 10, 15, 30, 50, 100]}
            />
          </CardFooter>
        </Card>
      </DataGrid>

      {/* Suspend Dialog */}
      <Dialog open={suspendDialog.open} onOpenChange={(open) => setSuspendDialog({ open, tenant: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Suspend Tenant</DialogTitle>
            <DialogDescription>
              Provide a reason for suspending {suspendDialog.tenant?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="reason">Reason</Label>
              <Textarea
                id="reason"
                value={suspendReason}
                onChange={(e) => setSuspendReason(e.target.value)}
                placeholder="Enter reason for suspension..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setSuspendDialog({ open: false, tenant: null });
                setSuspendReason('');
              }}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleSuspend}>
              Suspend Tenant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tenant Form Dialog */}
      <TenantFormSheet
        open={formDialog.open}
        onOpenChange={(open) => setFormDialog({ open, tenant: null })}
        tenant={formDialog.tenant}
      />
    </>
  );
}

export { TenantsListPage };
